import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import type { Group } from '@/services/groupService'
import { groupService } from '@/services/groupService'

interface UseGroupsResult {
  groups: Group[]
  isLoading: boolean
  isRefetching: boolean
  error: string | null
  refetch: () => Promise<void>
}

const GROUPS_CACHE_KEY = 'badminton-leagues:groups-cache:v1'

function readCachedGroups(): Group[] {
  try {
    const raw = sessionStorage.getItem(GROUPS_CACHE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as Group[]) : []
  } catch {
    return []
  }
}

function writeCachedGroups(groups: Group[]) {
  try {
    sessionStorage.setItem(GROUPS_CACHE_KEY, JSON.stringify(groups))
  } catch {
    // ignore storage failures
  }
}

function clearCachedGroups() {
  try {
    sessionStorage.removeItem(GROUPS_CACHE_KEY)
  } catch {
    // ignore storage failures
  }
}

export function useGroups(): UseGroupsResult {
  const { isAuthenticated, isLoading: authLoading } = useAuth0()
  
  // Only use cached groups for authenticated users
  // This prevents stale cache from causing auth errors for public views
  const initialCached = useRef<Group[] | null>(null)
  if (initialCached.current === null) {
    initialCached.current = readCachedGroups()
  }

  // For non-authenticated users, always start with empty groups
  // This ensures public group pages don't incorrectly think user is a member
  const [groups, setGroups] = useState<Group[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefetching, setIsRefetching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hasLoadedOnce = useRef(false)
  const mountedRef = useRef(true)
  const requestSeqRef = useRef(0)

  const fetchGroups = useCallback(async (options?: { force?: boolean }) => {
    const requestSeq = ++requestSeqRef.current
    // Only show full loading on initial load, use isRefetching for subsequent fetches
    if (hasLoadedOnce.current) {
      setIsRefetching(true)
    } else {
      setIsLoading(true)
    }
    setError(null)
    
    try {
      const data = await groupService.list({ force: options?.force })
      if (!mountedRef.current || requestSeq !== requestSeqRef.current) return
      setGroups(data)
      writeCachedGroups(data)
      hasLoadedOnce.current = true
    } catch (err) {
      if (!mountedRef.current || requestSeq !== requestSeqRef.current) return
      setError(err instanceof Error ? err.message : 'Failed to load groups')
    } finally {
      if (!mountedRef.current || requestSeq !== requestSeqRef.current) return
      setIsLoading(false)
      setIsRefetching(false)
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    
    // Only fetch groups if user is authenticated
    if (!authLoading && isAuthenticated) {
      // Use cache for initial render if available
      if (initialCached.current && initialCached.current.length > 0 && !hasLoadedOnce.current) {
        setGroups(initialCached.current)
        hasLoadedOnce.current = true
        setIsLoading(false)
      }
      fetchGroups()
    } else if (!authLoading && !isAuthenticated) {
      // Not authenticated - clear cache and return empty
      clearCachedGroups()
      setIsLoading(false)
      setGroups([])
    }
    
    return () => {
      mountedRef.current = false
    }
  }, [fetchGroups, authLoading, isAuthenticated])

  return {
    groups,
    isLoading: authLoading || isLoading,
    isRefetching,
    error,
    refetch: () => fetchGroups({ force: true }),
  }
}

